import { useCallback, useState } from "react";

export type Location = {
    latitude: number;
    longitude: number;
};

export type LocationErrorCode =
    | "denied"
    | "unavailable"
    | "timeout"
    | "unsupported";

export type LocationError = {
    code: LocationErrorCode;
    message: string;
};

// GeolocationPositionError codes: 1 = denied, 2 = unavailable, 3 = timeout
const ERROR_CODES: Record<number, LocationErrorCode> = {
    1: "denied",
    2: "unavailable",
    3: "timeout",
};

const useCurrentLocation = () => {
    const [location, setLocation] = useState<Location>();
    const [error, setError] = useState<LocationError>();
    const [locating, setLocating] = useState(false);

    // Resolves with the location, or undefined when it can't be determined
    // (the reason is then in error).
    const locate = useCallback(
        () =>
            new Promise<Location | undefined>((resolve) => {
                setError(undefined);

                if (!navigator.geolocation) {
                    setError({
                        code: "unsupported",
                        message: "Geolocation is not supported.",
                    });
                    resolve(undefined);
                    return;
                }

                setLocating(true);
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude, longitude } = position.coords;
                        const current = { latitude, longitude };
                        setLocation(current);
                        setLocating(false);
                        resolve(current);
                    },
                    (positionError) => {
                        setError({
                            code:
                                ERROR_CODES[positionError.code] ??
                                "unavailable",
                            message: positionError.message,
                        });
                        setLocating(false);
                        resolve(undefined);
                    },
                    {
                        maximumAge: 30 * 1000,
                        timeout: 30 * 1000,
                        enableHighAccuracy: true,
                    }
                );
            }),
        []
    );

    return { location, error, locating, locate };
};

export default useCurrentLocation;
